import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  usePropertyField,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { isPriceGuaranteeEnabled } from '../lib/priceGuarantee';

const DOCK_HEIGHT_VAR = '--pgp-dock-height';

function setDockHeight(px: number) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(DOCK_HEIGHT_VAR, `${Math.max(0, Math.round(px))}px`);
}

function clearDockHeight() {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty(DOCK_HEIGHT_VAR);
}

/**
 * Sitewide bottom tab/panel for AMC “True Price Guarantee” properties.
 * Gated by custom_show_price_guarantee. Matches the Duda slide-up:
 * “Simple. Transparent. All-Inclusive Monthly Pricing.”
 *
 * Publishes `--pgp-dock-height` so SpecialOfferBanner’s floating reopen pill
 * can sit above this dock instead of colliding at bottom-center.
 */
export function PriceGuaranteePopup() {
  const enabled = isPriceGuaranteeEnabled(usePropertyField('custom_show_price_guarantee'));
  const tabLabel = useContentToken('priceguaranteepopup.tab_label');
  const title = useContentToken('priceguaranteepopup.title');
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Signal open state so Footer can drop OmniChannel behind this panel.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!enabled) {
      document.documentElement.removeAttribute('data-pgp-open');
      return;
    }
    document.documentElement.setAttribute('data-pgp-open', open ? 'true' : 'false');
    return () => document.documentElement.removeAttribute('data-pgp-open');
  }, [enabled, open]);

  // Keep Special Offer reopen / OmniChannel clear of this stack.
  useEffect(() => {
    if (!enabled) {
      clearDockHeight();
      return;
    }
    const el = rootRef.current;
    if (!el) return;

    const measure = () => {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        setDockHeight(0);
        return;
      }
      setDockHeight(el.getBoundingClientRect().height);
    };
    measure();
    // Panel open animates max-height — remeasure after the transition.
    const t = window.setTimeout(measure, open ? 380 : 0);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      window.clearTimeout(t);
      ro.disconnect();
      window.removeEventListener('resize', measure);
      clearDockHeight();
    };
  }, [enabled, open, tabLabel]);

  if (!enabled) return null;

  return (
    <div ref={rootRef} className="pgp" data-open={open ? 'true' : 'false'}>
      {!open && (
        <button
          type="button"
          className="pgp-tab"
          aria-expanded={false}
          aria-controls={panelId}
          onClick={toggle}
        >
          <span className="pgp-tab-chevron" aria-hidden="true">
            <svg viewBox="0 0 448 512" className="h-3 w-3 fill-current">
              <path d="M207 381.5L12.7 187.1c-9.4-9.4-9.4-24.6 0-33.9l22.7-22.7c9.4-9.4 24.5-9.4 33.9 0L224 284.5l154.7-154c9.4-9.3 24.5-9.3 33.9 0l22.7 22.7c9.4 9.4 9.4 24.6 0 33.9L241 381.5c-9.4 9.4-24.6 9.4-33.9 0z" />
            </svg>
          </span>
          <EditableToken
            path="priceguaranteepopup.tab_label"
            defaultValue="Simple. Transparent. All-Inclusive Monthly Pricing."
            as="span"
            className="pgp-tab-label pgp-tab-label-full"
          >
            {tabLabel || 'Simple. Transparent. All-Inclusive Monthly Pricing.'}
          </EditableToken>
          <span className="pgp-tab-label pgp-tab-label-short">
            All-Inclusive monthly pricing
          </span>
        </button>
      )}

      <div
        id={panelId}
        className="pgp-panel"
        role="dialog"
        aria-modal="false"
        aria-label={String(title || tabLabel || 'Price guarantee')}
      >
        <button type="button" className="pgp-close" aria-label="Close" onClick={close}>
          &times;
        </button>
        <div className="pgp-panel-inner">
          <EditableToken
            path="priceguaranteepopup.title"
            defaultValue="Simple. Transparent. All-Inclusive Monthly Pricing."
            as="h2"
            className="pgp-title font-heading"
          >
            {title || 'Simple. Transparent. All-Inclusive Monthly Pricing.'}
          </EditableToken>
          <RichTextContent
            path="priceguaranteepopup.body"
            defaultValue={
              '<p>We believe your rent should be effortless and easy to understand. Your advertised rate thoughtfully includes mandatory monthly fees—so there are no hidden costs or surprise add-ons. What you see is truly what you pay.</p><p>Optional services and variable utilities are outlined separately in our Fee Transparency Guide, giving you flexibility and control. Optional offerings may be selected at your discretion, while utilities are billed as required.</p>'
            }
            as="div"
            className="pgp-body font-body"
          />
        </div>
      </div>

      <style>{`
        .pgp {
          position: fixed;
          left: 50%;
          bottom: 0;
          transform: translateX(-50%);
          z-index: 80;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: min(36rem, calc(100vw - 1.5rem));
          pointer-events: none;
        }
        /* Open panel sits above OmniChannel (vendor z-index is huge). */
        .pgp[data-open="true"] {
          z-index: 100000;
        }
        .pgp-tab,
        .pgp-panel,
        .pgp-close {
          pointer-events: auto;
        }
        .pgp-tab {
          order: 2;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          max-width: 100%;
          padding: 0.65rem 1rem 0.75rem;
          border: 1px solid #eae4e4;
          border-bottom: none;
          border-radius: 0.5rem 0.5rem 0 0;
          background: #fff;
          color: #1a1a1a;
          box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
          cursor: pointer;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          line-height: 1.25;
          text-align: left;
        }
        /* Point up — panel opens upward from the bottom tab. */
        .pgp-tab-chevron {
          display: inline-flex;
          flex-shrink: 0;
          transform: rotate(180deg);
        }
        .pgp-tab-label {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .pgp-tab-label-short {
          display: none;
        }
        .pgp-panel {
          order: 1;
          width: 100%;
          border: 1px solid #eae4e4;
          border-radius: 0.75rem 0.75rem 0 0;
          background: #fff;
          box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.12);
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.35s ease, opacity 0.25s ease;
        }
        .pgp[data-open="true"] .pgp-panel {
          max-height: min(70vh, 28rem);
          opacity: 1;
          overflow-y: auto;
          pointer-events: auto;
          /* Tab is hidden while open — dock the panel flush to the bottom. */
          border-bottom: none;
          margin-bottom: 0;
        }
        .pgp:not([data-open="true"]) .pgp-panel {
          pointer-events: none;
        }
        .pgp-close {
          position: absolute;
          top: 0.5rem;
          right: 0.65rem;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border: none;
          border-radius: 9999px;
          background: transparent;
          color: #1a1a1a;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
        }
        .pgp-close:hover {
          background: var(--vibe-color-surface);
        }
        .pgp-panel-inner {
          position: relative;
          padding: 1.25rem 1.5rem 1.5rem;
        }
        .pgp-title {
          margin: 0 2rem 0.85rem 0;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--vibe-color-text);
        }
        .pgp-body {
          font-size: 0.9rem;
          line-height: 1.55;
          color: var(--vibe-color-text);
        }
        .pgp-body p {
          margin: 0 0 0.75rem;
        }
        .pgp-body p:last-child {
          margin-bottom: 0;
        }
        /* Phone + tablet: shorter tab copy. */
        @media (max-width: 1024px) {
          .pgp-tab-label-full {
            display: none !important;
          }
          .pgp-tab-label-short {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .pgp-tab {
            font-size: 0.75rem;
            padding: 0.55rem 0.85rem 0.65rem;
          }
          .pgp-panel-inner {
            padding: 1rem 1.1rem 1.25rem;
          }
        }

        /* Phone: park under OmniChannel (right); leave AudioEye + Special Offer clear. */
        @media (max-width: 640px) {
          .pgp {
            left: auto;
            right: 0.75rem;
            transform: none;
            align-items: stretch;
            width: min(15.5rem, calc(100vw - 5.5rem));
          }
          .pgp[data-open="true"] {
            left: 0.75rem;
            right: 0.75rem;
            width: auto;
          }
        }

        /* Tablet: center the tab (Special Offer centers too); OmniChannel still
           lifts via --pgp-dock-height. */
        @media (min-width: 641px) and (max-width: 1024px) {
          .pgp {
            left: 50%;
            right: auto;
            transform: translateX(-50%);
            align-items: center;
            width: min(20rem, calc(100vw - 2rem));
          }
          .pgp[data-open="true"] {
            width: min(36rem, calc(100vw - 1.5rem));
          }
        }
      `}</style>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PriceGuaranteePopup',
  displayName: 'Price Guarantee Popup',
  tokens: {
    'priceguaranteepopup.tab_label': {
      label: 'Tab Label',
      type: 'text',
      defaultValue: 'Simple. Transparent. All-Inclusive Monthly Pricing.',
    },
    'priceguaranteepopup.title': {
      label: 'Panel Title',
      type: 'text',
      defaultValue: 'Simple. Transparent. All-Inclusive Monthly Pricing.',
    },
    'priceguaranteepopup.body': {
      label: 'Panel Body',
      type: 'richtext',
      defaultValue:
        '<p>We believe your rent should be effortless and easy to understand. Your advertised rate thoughtfully includes mandatory monthly fees—so there are no hidden costs or surprise add-ons. What you see is truly what you pay.</p><p>Optional services and variable utilities are outlined separately in our Fee Transparency Guide, giving you flexibility and control. Optional offerings may be selected at your discretion, while utilities are billed as required.</p>',
    },
  },
};

(PriceGuaranteePopup as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PriceGuaranteePopup;
